import {
  BodyText,
  Button,
  Card,
  Heading,
  IconSet,
  Input,
  Layout,
  TabPanel,
} from '@postscript/components';
import {
  StyledExampleText,
  StyledInputLayout,
  StyledTabPanelIntro,
  StyledTabPanelText,
  TokenExampleCard,
  TwoColumnLayout,
} from '../../Layout/SimpleStyledComponents';
import TokenBlock from '../../Layout/TokenBlock';
import { FoundationsProps } from '../Foundations';

const FoundationsPatterns = ({ selected }: FoundationsProps): JSX.Element => {
  return (
    <TabPanel id="foundations-patterns" selected={selected}>
      <StyledTabPanelIntro>
        <BodyText size="medium">
          As a design team, we take note of reusable patterns or guidelines that
          provide the best solutions for our users. Current Postscript design
          patterns are documented here.
        </BodyText>
      </StyledTabPanelIntro>

      <TwoColumnLayout margin="0 0 var(--spacing-4)">
        <StyledTabPanelText>
          <Heading size="xx-small">Border radius</Heading>
          <BodyText size="small">
            We use three levels of border-radius. From outer elements inward, we
            use large (24px), medium (12px), and small (6px). For instance, a
            larger container Card will have a large border-radius, while the
            content inside that card would be medium to small.
          </BodyText>

          <Heading size="xx-small">Supplementary border radius</Heading>
          <BodyText size="small">
            Currently, some small elements such as form fields use one level
            smaller: an extra-small 3px. This is used to create a slightly
            off-round effect for certain small elements. In addition,
            you&apos;ll find a variable used to make corners a consistently
            round 999px. These styles are very specific and should not be used
            as a practice.
          </BodyText>
        </StyledTabPanelText>
        <Card size="small">
          <Heading size="xx-small">Tokens and CSS variables</Heading>
          <TokenBlock
            name="Small border radius"
            description="A small 6px radius is good for small items and tight spaces, or a simple softening of an element."
            variable="border-radius-small"
            example={
              <TokenExampleCard
                style={{ borderRadius: 'var(--border-radius-small)' }}
              >
                6px
              </TokenExampleCard>
            }
          />
          <TokenBlock
            name="Medium border radius"
            description="A 12px radius is a strong default."
            variable="border-radius-medium"
            example={
              <TokenExampleCard
                style={{ borderRadius: 'var(--border-radius-medium)' }}
              >
                12px
              </TokenExampleCard>
            }
          />
          <TokenBlock
            name="Large border radius"
            description="A 24px radius is great for large Cards or containing items with smaller radii."
            variable="border-radius-large"
            example={
              <TokenExampleCard
                style={{ borderRadius: 'var(--border-radius-large)' }}
              >
                24px
              </TokenExampleCard>
            }
          />
          <TokenBlock
            name="(Extra) small border radius"
            description="3px adds a slightly rounded effect. Use this very sparingly."
            variable="border-radius-x-small"
            example={
              <TokenExampleCard
                style={{ borderRadius: 'var(--border-radius-x-small)' }}
              >
                3px
              </TokenExampleCard>
            }
          />
          <TokenBlock
            name="Round border radius"
            description="A massive border radius that will round most any element."
            variable="border-radius-round"
            example={
              <TokenExampleCard
                style={{ borderRadius: 'var(--border-radius-round)' }}
              >
                999px
              </TokenExampleCard>
            }
          />
        </Card>
      </TwoColumnLayout>

      <TwoColumnLayout margin="0 0 var(--spacing-4)">
        <StyledTabPanelText>
          <Heading size="xx-small">Box shadow and elevation</Heading>
          <BodyText size="small">
            Box shadows indicate elevation between elements, separate containers
            from similar backgrounds, and afford the interactivity of an
            element. Elevation is not a large factor in our designs at
            Postscript. However, a single layer of elevation is necessary for
            Card layouts, providing a sense of depth and contrast. Typically,
            stick to the small box-shadow listed here, raising elevation only to
            contrast with the plane beneath.
          </BodyText>
        </StyledTabPanelText>
        <Card size="small">
          <Heading size="xx-small">Tokens and CSS variables</Heading>
          <TokenBlock
            name="Low elevation"
            description="Small elevation documentation"
            variable="box-shadow-small"
            example={
              <TokenExampleCard
                style={{
                  borderRadius: 'var(--border-radius-small)',
                  boxShadow: 'var(--box-shadow-small)',
                }}
              >
                Low
              </TokenExampleCard>
            }
          />
          <TokenBlock
            name="Medium elevation"
            description="Medium elevation documentation"
            variable="box-shadow-medium"
            example={
              <TokenExampleCard
                style={{
                  borderRadius: 'var(--border-radius-small)',
                  boxShadow: 'var(--box-shadow-medium)',
                }}
              >
                Medium
              </TokenExampleCard>
            }
          />
          <TokenBlock
            name="Large elevation"
            description="Large elevation documentation"
            variable="box-shadow-large"
            example={
              <TokenExampleCard
                style={{
                  borderRadius: 'var(--border-radius-small)',
                  boxShadow: 'var(--box-shadow-large)',
                }}
              >
                Large
              </TokenExampleCard>
            }
          />
        </Card>
      </TwoColumnLayout>

      <TwoColumnLayout margin="0 0 var(--spacing-4)">
        <StyledTabPanelText>
          <Heading size="xx-small">Buttons</Heading>
          <BodyText size="small">
            Limit the number of Buttons on a page. Every page should serve a
            purpose. Aim for one Primary Button, followed by Secondary and
            tertiary Text Buttons. When grouping buttons together, follow
            hierarchy rules. Pair a Primary action with a Secondary action or
            Text Button. Include appropriate space between, placing the higher
            import Button on the righthand side (no hate please).
          </BodyText>
        </StyledTabPanelText>
        <Card
          size="small"
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
          }}
        >
          <Layout align="right">
            <Button variant="secondary">Secondary Action</Button>
            <Button>Primary Action</Button>
          </Layout>
        </Card>
      </TwoColumnLayout>

      <TwoColumnLayout margin="0 0 var(--spacing-4)">
        <StyledTabPanelText
          style={{
            maxWidth: '430px',
          }}
        >
          <Heading size="xx-small">Adding and removing</Heading>
          <BodyText size="small">
            When adding an item that uses pre-existing information, use a text
            button with the following structure: &quot;+ Add [Item]&quot;
            <StyledExampleText>
              See &quot;+ Add Rule&quot; in the example.
            </StyledExampleText>
          </BodyText>

          <BodyText size="small">
            When creating something new or adding a complex group, use a text
            button with the following structure: &quot;+ Create [XXX]&quot;
            <StyledExampleText>
              See &quot;+ Create Filter&quot; in the example.
            </StyledExampleText>
          </BodyText>

          <BodyText size="small">
            When removing an item from a given area in the app, use the
            &quot;X&quot; icon in a text button next to the item.
            <StyledExampleText>
              See &quot;Rule 1&quot; and &quot;Rule 2&quot; inputs in the
              example.
            </StyledExampleText>
          </BodyText>

          <BodyText size="small">
            When deleting an item (causing it to no longer exist anywhere on the
            app), use the trash can icon in a text button next to the item.
          </BodyText>

          <BodyText size="small">
            When removing or deleting a group, use the action in a text button
            with the following structure: &quot;[Action] [Group]&quot;.
            <StyledExampleText>
              {' '}
              See &quot;Delete Filter&quot; in the example.
            </StyledExampleText>
          </BodyText>
        </StyledTabPanelText>
        <Card size="small">
          <Layout
            valign="top"
            vertical
            gap="var(--spacing-2)"
            style={{
              backgroundColor: 'var(--gray-1)',
              borderRadius: 'var(--border-radius-small)',
              padding: 'var(--spacing-2)',
              margin: '0 0 var(--spacing-2)',
            }}
          >
            <Layout
              align="right"
              style={{
                width: '100%',
              }}
            >
              <Button variant="text" size="small">
                Delete Filter
              </Button>
            </Layout>

            <StyledInputLayout>
              <Input
                value="Rule 1"
                fieldSize="small"
                onChange={() => undefined}
              />
              <Button variant="text" icon={IconSet.Close} />
            </StyledInputLayout>

            <BodyText size="small">AND</BodyText>

            <StyledInputLayout>
              <Input
                value="Rule 2"
                fieldSize="small"
                onChange={() => undefined}
              />
              <Button variant="text" icon={IconSet.Close} />
            </StyledInputLayout>

            <Button variant="text" size="small" icon={IconSet.Plus}>
              Add Rule
            </Button>
          </Layout>
          <Button variant="text" size="small" icon={IconSet.Plus}>
            Create Filter
          </Button>
        </Card>
      </TwoColumnLayout>

      <TwoColumnLayout margin="0 0 var(--spacing-4)">
        <StyledTabPanelText>
          <Heading size="xx-small">Date and time formatting</Heading>
          <BodyText size="small">
            Always use clear, human-friendly date and time formats. Prefer
            &quot;Jan 2, 2024&quot; for dates and &quot;3:45 PM&quot; for times.
            Avoid numeric-only formats like &quot;01/02/24&quot; which can be
            ambiguous. Use relative time (&quot;5 minutes ago&quot;) for recent
            events, and include time zones if the user&apos;s context is not
            obvious.
          </BodyText>
          <BodyText size="small">
            For more details, see our{' '}
            <a
              href="https://components.postscript.io/?path=/docs/design-guidance-date-and-time-formatting--docs"
              target="_blank"
              rel="noopener noreferrer"
            >
              Date and Time Formatting Guidance
            </a>
            .
          </BodyText>
        </StyledTabPanelText>
        <Card size="small">
          <Heading size="xx-small">Examples</Heading>
          <BodyText size="small">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Jan 2, 2024</li>
              <li>3:45 PM</li>
              <li>5 minutes ago</li>
              <li>Jan 2, 2024, 3:45 PM (PST)</li>
            </ul>
          </BodyText>
        </Card>
      </TwoColumnLayout>
    </TabPanel>
  );
};

export default FoundationsPatterns;
